import {
  Bell,
  Gift,
  SquareCheckBig,
  Mail,
  Calendar,
  Newspaper,
  MapPin,
  Phone,
  DollarSign,
  Shield,
  MessageSquare,
  Star,
  Wallet,
  GitBranch,
  MessageCircle,
} from "lucide-react";
import { type ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useBrand } from "src/hooks/useBrand";

import {
  type ChannelSelection,
  type ConsentPreferences,
} from "./consent-preferences";

export type PurposeDefinition = {
  id: keyof ConsentPreferences;
  title: string;
  description: string;
  icon: ReactElement;
  tipIcon?: ReactElement;
  tip?: ReactElement;
};

export type ChannelDefinition = {
  id: keyof ChannelSelection;
  label: string;
  icon: ReactElement;
};

export function useConsentContent(): {
  purposes: PurposeDefinition[];
  channels: ChannelDefinition[];
} {
  const { t } = useTranslation();
  const { customerPortal } = useBrand();

  const purposes: Array<PurposeDefinition> = [
    {
      id: "personalizedOffers",
      title: t("Offres et communications personnalisées"),
      description: t(
        "Communications adaptées à votre profil, vos préférences et votre situation.",
      ),
      icon: <Wallet />,
      tipIcon: <DollarSign />,
      tip: (
        <Trans i18nKey="<strong>Réalisez des économies au quotidien</strong> grâce aux conseils de nos experts et aux offres exclusives de nos partenaires de confiance (énergie, télécom, travaux, mobilité…)." />
      ),
    },
    {
      id: "partnerOffers",
      title: t("Offres des filiales Emeria & partenaires"),
      description: t(
        "Offres exclusives de nos partenaires de confiance (énergie, télécom, travaux, mobilité, sécurité…) via la marketplace.",
      ),
      icon: <Gift />,
    },
    {
      id: "propertyNews",
      title: t("Actualités immobilières"),
      description: t(
        "Informations et actualités liées au marché immobilier et à nos services.",
      ),
      icon: <Newspaper />,
      tipIcon: <SquareCheckBig />,
      tip: (
        <Trans i18nKey="<strong>Désinscription en 1 clic.</strong> Un lien de désabonnement est présent dans chacune de nos communications. Vous gardez la main, sans démarche compliquée." />
      ),
    },
    {
      id: "events",
      title: t("Événements et webinaires"),
      description: t(
        "Invitations à des événements, webinaires ou rencontres organisés par nos équipes.",
      ),
      icon: <Calendar />,
      tipIcon: <MapPin />,
      tip: (
        <Trans i18nKey="<strong>Vos données hébergées en France.</strong> Toutes vos informations sont stockées et traitées sur des serveurs situés en France." />
      ),
    },
    {
      id: "promotions",
      title: t("Jeux concours et promotions"),
      description: t(
        "Organisation de jeux, opérations promotionnelles et offres spéciales.",
      ),
      icon: <Star />,
    },
    {
      id: "propertyAlerts",
      title: t("Alertes immobilières"),
      description: t(
        "Notifications concernant des biens ou opportunités correspondant à vos critères.",
      ),
      icon: <Bell />,
      tipIcon: <Shield />,
      tip: (
        <Trans
          i18nKey="<strong>Vous restez maître de vos données.</strong> À tout moment, vous pouvez modifier, suspendre ou retirer chacun de vos consentements depuis votre espace {{portalName}} — finalité par finalité, canal par canal."
          values={{ portalName: customerPortal.name }}
        />
      ),
    },
    {
      id: "satisfactionSurveys",
      title: t("Enquêtes de satisfaction"),
      description: t(
        "Sollicitations visant à recueillir votre avis afin d'améliorer nos services.",
      ),
      icon: <GitBranch />,
    },
  ];

  const channels = [
    { id: "email", label: t("Email"), icon: <Mail /> },
    { id: "sms", label: t("SMS"), icon: <MessageSquare /> },
    { id: "whatsapp", label: t("WhatsApp"), icon: <MessageCircle /> },
    { id: "phone", label: t("Téléphone"), icon: <Phone /> },
  ] satisfies Array<{
    id: keyof ChannelSelection;
    label: string;
    icon: ReactElement;
  }>;

  return { purposes, channels };
}
